import { restrictionRuleText, uncheckedLegInfo, waypointLabel } from "@/utils/routeHelper.jsx";
import { num } from "@/features/route/transits.js";

// Leg-bound warnings of the leg wp1 → wp2 as LegCardBody issues, red before orange.
export function legIssues(properties) {
  const w = properties?.warnings ?? {};
  const rnw = properties?.rnw_evaluation ?? properties?.rnwEvaluation ?? {};
  const sog = num(properties?.sog);
  const issues = [];
  if (w.warning_is_closed_viaarea === true)
    issues.push({ kind: "closed_viaarea", level: "warning", title: "Closed via area", detail: "Route passes through a closed via area" });
  if (w.warning_leg_usable === true)
    issues.push({ kind: "leg_unusable", level: "warning", title: "Leg not usable", detail: restrictionRuleText(properties?.restrictions) });
  // astar sets it only when the route travels a leg against its usage direction.
  if (w.warning_is_free_usage === true) {
    const usage = properties?.leg_data?.usage;
    issues.push(
      usage === 0
        ? { kind: "usage", level: "warning", title: "Wrong-way leg", detail: "Against the one-way direction" }
        : { kind: "usage", level: "warning", title: "Usage restriction", detail: usage != null ? `Leg usage ${usage}` : null },
    );
  }
  if (w.warning_vertical_clearance === true)
    issues.push({ kind: "vertical_clearance", level: "warning", title: "Vertical clearance", value: num(properties?.leg_data?.vertical_clearance), unit: "m" });
  if (w.warning_water_depth === true)
    issues.push({ kind: "water_depth", level: "warning", title: "Water depth", detail: rnw.ww_nav_cat != null ? `Nav category ${rnw.ww_nav_cat}` : "Insufficient for the vessel" });
  if (rnw.ww_maxspeed_knots != null)
    issues.push({ kind: "max_speed", level: "info", title: "Speed limit", limit: num(rnw.ww_maxspeed_knots), value: sog, unit: "kn" });
  if (rnw.ww_minspeed_knots != null)
    issues.push({ kind: "min_speed", level: "info", title: "Minimum speed", limit: num(rnw.ww_minspeed_knots), value: sog, unit: "kn" });
  if (uncheckedLegInfo(w))
    issues.push({ kind: "unchecked", level: "info", title: "Unchecked leg", detail: "Not checked for land or port areas" });
  return issues;
}

const endpoint = (wp) =>
  wp
    ? {
        time: wp.properties?.timestamp ?? null,
        leg: waypointLabel(wp.properties),
        position: wp.geometry?.coordinates ?? null,
      }
    : undefined;

export function legCardProps(wp1, wp2) {
  const from = endpoint(wp1);
  const to = endpoint(wp2);
  const hours =
    from?.time && to?.time ? (new Date(to.time) - new Date(from.time)) / 3600000 : null;
  return {
    issues: legIssues(wp1?.properties),
    from,
    to,
    durationH: Number.isFinite(hours) && hours >= 0 ? hours : undefined,
  };
}

const oneDecimal = (v) => (v == null ? "–" : String(Number(v.toFixed(1))));
// Whose detail only repeats the title.
const TITLE_ONLY = new Set(["closed_viaarea", "unchecked"]);

// One-line text of a leg issue for compact lists (waypoint table issues).
export function legIssueLabel(issue) {
  if (issue.kind === "max_speed") return `RNW speed limit ${oneDecimal(issue.limit)} kn`;
  if (issue.kind === "min_speed") return `RNW minimum speed ${oneDecimal(issue.limit)} kn`;
  if (TITLE_ONLY.has(issue.kind)) return issue.title;
  const extra = issue.value != null ? `${oneDecimal(issue.value)} ${issue.unit}` : issue.detail;
  return [issue.title, extra].filter(Boolean).join(" · ");
}
